export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  categoryLabel: string;
  image: string;
  fullStory: Array<{
    type: 'text' | 'quote';
    content: string;
  }>;
}

export const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'A Peaceful Home Birth Journey with My Doula by My Side',
    excerpt: 'When I discovered I was pregnant with my second child, I knew I wanted something different. My first birth was in a hospital, and while it was safe, it felt clinical and rushed. This time, I wanted to feel the power of my body in my own space.',
    author: 'Sarah Mitchell',
    date: 'March 15, 2024',
    readTime: '8 min read',
    category: 'home-birth',
    categoryLabel: 'Home Birth',
    image: 'https://public.readdy.ai/ai/img_res/90b77f6d7eb2b079c745c7dbf0445e95.jpg',
    fullStory: [
      {
        type: 'text',
        content: 'When I discovered I was pregnant with my second child, I knew I wanted something different. My first birth was in a hospital, and while it was safe, it felt clinical and rushed. This time, I wanted to feel the power of my body in my own space, surrounded by love and calm.'
      },
      {
        type: 'text',
        content: 'I connected with Lotus Maternal early in my pregnancy, and from our first consultation, I felt seen and heard. My doula helped me prepare mentally, physically, and emotionally. We practiced breathing techniques, discussed my birth preferences, and she reassured me that my body was designed for this.'
      },
      {
        type: 'quote',
        content: 'The moment my doula arrived, I felt a wave of calm wash over me. She held space for me in a way no one else could.'
      },
      {
        type: 'text',
        content: 'Labor began gently in the early morning. I moved through contractions in my living room, swaying to soft music, sipping herbal tea, and feeling completely in control. My doula arrived just as things intensified, and her presence was like an anchor. She reminded me to breathe, offered counter-pressure during contractions, and whispered affirmations that kept me grounded.'
      },
      {
        type: 'text',
        content: 'When my daughter was born into my hands in the comfort of my bedroom, surrounded by candlelight and the people I love most, I felt an overwhelming sense of empowerment. This was my birth. My way. And I will forever be grateful for the support that made it possible.'
      },
      {
        type: 'text',
        content: 'To any mother considering a home birth: trust yourself. Trust your body. And find a team that believes in you as much as you believe in your baby. It changes everything.'
      }
    ]
  },
  {
    id: '2',
    title: 'My Hospital Birth: Supported, Empowered, and Heard',
    excerpt: 'I always knew I wanted to give birth in a hospital. As a first-time mom, I felt safest knowing medical support was immediately available. But I also wanted to feel empowered and in control of my experience.',
    author: 'Jessica Torres',
    date: 'March 10, 2024',
    readTime: '7 min read',
    category: 'hospital-birth',
    categoryLabel: 'Hospital Birth',
    image: 'https://readdy.ai/api/search-image?query=African%20American%20woman%20in%20hospital%20birth%20room%20with%20warm%20soft%20lighting%20comfortable%20atmosphere%20natural%20wood%20tones%20gentle%20cream%20walls%20peaceful%20medical%20setting%20with%20supportive%20doula%20presence%20empowering%20maternal%20care%20environment%20deep%20melanin%20skin%20tones%20calming%20ambiance&width=800&height=600&seq=blog-post-aa-v2-002&orientation=landscape',
    fullStory: [
      {
        type: 'text',
        content: 'I always knew I wanted to give birth in a hospital. As a first-time mom, I felt safest knowing medical support was immediately available. But I also wanted to feel empowered and in control of my experience. That\'s where my doula came in.'
      },
      {
        type: 'text',
        content: 'From the moment I arrived at the hospital, my doula was my advocate. She helped me communicate my birth plan to the nurses and doctors, reminded me of my options when decisions needed to be made, and never left my side. Even when things didn\'t go exactly as planned, I felt supported and informed every step of the way.'
      },
      {
        type: 'quote',
        content: 'Having a doula in the hospital didn\'t mean I didn\'t trust the medical team—it meant I had someone there just for me.'
      },
      {
        type: 'text',
        content: 'Labor was long and intense. There were moments I doubted myself, moments I wanted to give up. But my doula was there with cool washcloths, gentle words, and unwavering belief in my strength. She helped me move through positions, reminded me to breathe, and celebrated every centimeter of progress.'
      },
      {
        type: 'text',
        content: 'When my son was finally born, I felt an incredible sense of accomplishment. Yes, I was in a hospital. Yes, I had medical interventions. But I also had a voice. I had choices. And I had support that made all the difference.'
      },
      {
        type: 'text',
        content: 'To any mom planning a hospital birth: you can have both safety and empowerment. You can have medical care and emotional support. And having a doula by your side makes that possible.'
      }
    ]
  },
  {
    id: '3',
    title: 'My VBAC Victory: Reclaiming My Birth Experience',
    excerpt: 'After an emergency C-section with my first baby, I was told a vaginal birth might not be possible for me. But deep down, I knew I wanted to try. I wanted to experience the birth I had dreamed of.',
    author: 'Amanda Chen',
    date: 'March 5, 2024',
    readTime: '9 min read',
    category: 'vbac',
    categoryLabel: 'VBAC',
    image: 'https://readdy.ai/api/search-image?query=empowered%20African%20American%20woman%20with%20natural%20hair%20in%20peaceful%20natural%20setting%20surrounded%20by%20supportive%20doula%20warm%20golden%20light%20symbolizing%20triumph%20and%20reclaiming%20birth%20experience%20earth%20tones%20cream%20amber%20soft%20botanical%20elements%20victorious%20sacred%20atmosphere%20deep%20melanin%20skin%20tones&width=800&height=600&seq=blog-post-aa-v2-003&orientation=landscape',
    fullStory: [
      {
        type: 'text',
        content: 'After an emergency C-section with my first baby, I was told a vaginal birth might not be possible for me. But deep down, I knew I wanted to try. I wanted to experience the birth I had dreamed of, and I wanted to prove to myself that my body was capable.'
      },
      {
        type: 'text',
        content: 'Finding a supportive care team was crucial. My doula from Lotus Maternal believed in me from day one. She helped me research VBAC-friendly providers, prepared me for the emotional and physical challenges, and reminded me that my previous birth didn\'t define this one.'
      },
      {
        type: 'quote',
        content: 'My doula told me: Your body is not broken. Your birth story is not over. And she was right.'
      },
      {
        type: 'text',
        content: 'Labor started naturally at 39 weeks. I was nervous but determined. My doula arrived early and stayed with me through every contraction. She helped me stay calm when fear crept in, reminded me of my strength when I felt weak, and celebrated every milestone with me.'
      },
      {
        type: 'text',
        content: 'When I finally pushed my daughter into the world, I cried tears of joy, relief, and triumph. I did it. My body did it. And I will never forget the moment my doula whispered, "You are so strong. You did this."'
      },
      {
        type: 'text',
        content: 'To any mother hoping for a VBAC: it is possible. Your body is capable. And with the right support, you can reclaim your birth experience. Don\'t let anyone tell you otherwise.'
      }
    ]
  },
  {
    id: '4',
    title: 'Postpartum Support Saved Me: My Journey Through the Fourth Trimester',
    excerpt: 'No one told me how hard postpartum would be. Everyone focused on the birth, but no one prepared me for the overwhelming exhaustion, the emotional rollercoaster, and the loneliness I would feel.',
    author: 'Mia Johnson',
    date: 'February 28, 2024',
    readTime: '6 min read',
    category: 'postpartum',
    categoryLabel: 'Postpartum',
    image: 'https://readdy.ai/api/search-image?query=tender%20African%20American%20new%20mother%20resting%20peacefully%20in%20soft%20natural%20light%20surrounded%20by%20supportive%20care%20gentle%20nurturing%20atmosphere%20warm%20earth%20tones%20cream%20linens%20botanical%20elements%20calming%20amber%20glow%20representing%20fourth%20trimester%20healing%20deep%20melanin%20skin%20tones%20maternal%20support&width=800&height=600&seq=blog-post-aa-v2-004&orientation=landscape',
    fullStory: [
      {
        type: 'text',
        content: 'No one told me how hard postpartum would be. Everyone focused on the birth, but no one prepared me for the overwhelming exhaustion, the emotional rollercoaster, and the loneliness I would feel in those first weeks with my newborn.'
      },
      {
        type: 'text',
        content: 'I was struggling. I loved my baby, but I was drowning. That\'s when I reached out to Lotus Maternal for postpartum support. My doula came to my home, held my baby while I showered, listened to me cry without judgment, and reminded me that what I was feeling was normal.'
      },
      {
        type: 'quote',
        content: 'She told me: You are not failing. You are healing. You are learning. And you are doing an incredible job.'
      },
      {
        type: 'text',
        content: 'Having postpartum support wasn\'t just about practical help—though the meal prep, baby soothing tips, and breastfeeding guidance were lifesaving. It was about having someone who saw me as a whole person, not just a mother. Someone who validated my feelings and reminded me that I mattered too.'
      },
      {
        type: 'text',
        content: 'Slowly, with support, I found my rhythm. I learned to ask for help. I learned to rest without guilt. And I learned that postpartum care is just as important as prenatal care.'
      },
      {
        type: 'text',
        content: 'To any new mother struggling: you are not alone. Postpartum is hard, and it\'s okay to need support. Reach out. Ask for help. You deserve to be cared for too.'
      }
    ]
  },
  {
    id: '5',
    title: 'Finding Strength in Surrender: My Unplanned C-Section Story',
    excerpt: 'I had planned a natural, unmedicated birth. I had practiced hypnobirthing, hired a doula, and felt prepared. But birth had other plans for me.',
    author: 'Rachel Green',
    date: 'February 20, 2024',
    readTime: '7 min read',
    category: 'hospital-birth',
    categoryLabel: 'Hospital Birth',
    image: 'https://readdy.ai/api/search-image?query=peaceful%20African%20American%20mother%20holding%20newborn%20baby%20in%20soft%20warm%20hospital%20recovery%20room%20light%20surrounded%20by%20supportive%20medical%20care%20gentle%20nurturing%20atmosphere%20cream%20tones%20natural%20wood%20elements%20calming%20ambiance%20representing%20strength%20and%20surrender%20in%20birth%20journey%20deep%20melanin%20skin%20tones&width=800&height=600&seq=blog-post-aa-v2-005&orientation=landscape',
    fullStory: [
      {
        type: 'text',
        content: 'I had planned a natural, unmedicated birth. I had practiced hypnobirthing, hired a doula, and felt prepared. But birth had other plans for me. After 20 hours of labor, my baby\'s heart rate dropped, and I was rushed into an emergency C-section.'
      },
      {
        type: 'text',
        content: 'I felt like I had failed. Like my body had let me down. But my doula stayed with me through it all. She held my hand during the surgery, helped me process my emotions afterward, and reminded me that I didn\'t fail—I adapted. I made the best decision for my baby.'
      },
      {
        type: 'quote',
        content: 'Birth is not about following a plan. It\'s about bringing your baby safely into the world, however that happens.'
      },
      {
        type: 'text',
        content: 'Recovery was hard, both physically and emotionally. But having postpartum support made all the difference. My doula visited me at home, helped me process my birth experience, and reminded me that a C-section is still birth. I still labored. I still brought my baby into the world. And I am still strong.'
      },
      {
        type: 'text',
        content: 'It took time, but I eventually found peace with my birth story. It wasn\'t what I planned, but it was mine. And my baby is healthy, loved, and here—and that\'s what matters most.'
      },
      {
        type: 'text',
        content: 'To any mother grieving an unplanned birth: your feelings are valid. It\'s okay to mourn the birth you didn\'t have. But please know—you are strong, you are brave, and you are an incredible mother.'
      }
    ]
  },
  {
    id: '6',
    title: 'A Water Birth at Home: My Most Peaceful Experience',
    excerpt: 'I had always been drawn to the idea of a water birth. The thought of laboring in warm water, feeling weightless and calm, resonated deeply with me.',
    author: 'Lily Martinez',
    date: 'February 15, 2024',
    readTime: '8 min read',
    category: 'home-birth',
    categoryLabel: 'Home Birth',
    image: 'https://readdy.ai/api/search-image?query=serene%20African%20American%20woman%20with%20natural%20hair%20in%20peaceful%20home%20water%20birth%20setting%20birthing%20pool%20surrounded%20by%20soft%20candlelight%20natural%20botanical%20elements%20warm%20amber%20glow%20tranquil%20sacred%20space%20for%20natural%20childbirth%20earth%20tones%20cream%20deep%20melanin%20skin%20tones%20calming%20minimalist%20aesthetic&width=800&height=600&seq=blog-post-aa-v2-006&orientation=landscape',
    fullStory: [
      {
        type: 'text',
        content: 'I had always been drawn to the idea of a water birth. The thought of laboring in warm water, feeling weightless and calm, resonated deeply with me. When I became pregnant with my third child, I knew this was the birth experience I wanted.'
      },
      {
        type: 'text',
        content: 'My doula helped me prepare every detail. We set up a birthing pool in my living room, created a playlist of calming music, and gathered all the supplies we would need. She reassured me that my body knew what to do and that she would be there to support me every step of the way.'
      },
      {
        type: 'quote',
        content: 'The moment I stepped into the warm water, I felt my entire body relax. It was like the water was holding me.'
      },
      {
        type: 'text',
        content: 'Labor progressed beautifully. The water eased the intensity of contractions, and I felt completely in tune with my body. My doula sat beside the pool, offering sips of water, gentle encouragement, and reminders to breathe. My partner held my hand, and my older children watched in awe from the doorway.'
      },
      {
        type: 'text',
        content: 'When my son was born into the water, I reached down and lifted him to my chest. He was calm, alert, and perfect. It was the most peaceful, empowering moment of my life. I felt like a goddess—strong, capable, and deeply connected to the ancient power of birth.'
      },
      {
        type: 'text',
        content: 'To any mother considering a water birth: it is magical. It is powerful. And with the right support, it can be one of the most beautiful experiences of your life.'
      }
    ]
  }
];